import api from "@/lib/api";

// Chunk size: 10 MB — pequeno o suficiente para passar em ingress/proxy padrão
// e grande o suficiente para não gerar centenas de requests para arquivos ~1 GB.
const CHUNK_SIZE = 10 * 1024 * 1024;
const CHUNK_TIMEOUT_MS = 120_000; // 2 min por chunk
const CHUNK_RETRIES = 3;

async function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Envia 1 arquivo em pedaços ao backend.
 * @param {File} file – o arquivo a enviar
 * @param {string} bookId – id do curso destino
 * @param {(percent:number, phase:string)=>void} onProgress – callback (0..100)
 * @returns {Promise<object>} – o media doc criado
 */
export async function uploadFileChunked(file, bookId, onProgress) {
  const report = (p, phase) => { if (onProgress) onProgress(Math.max(0, Math.min(100, Math.round(p))), phase); };
  report(0, "init");

  // 1) init
  const { data: init } = await api.post("/media/chunk/init", {
    book_id: bookId,
    filename: file.name,
    total_size: file.size,
    content_type: file.type || "application/octet-stream",
  }, { timeout: 30_000 });

  const sessionId = init.session_id;
  const chunkSize = init.chunk_size_recommendation || CHUNK_SIZE;
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

  try {
    // 2) upload de cada chunk sequencial com retries
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("chunk_index", String(i));
      fd.append("chunk", blob, `chunk_${i}`);

      let lastErr = null;
      for (let attempt = 0; attempt < CHUNK_RETRIES; attempt++) {
        try {
          await api.post("/media/chunk/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: CHUNK_TIMEOUT_MS,
          });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          // Backoff: 1s, 2s, 4s
          await sleep(1000 * Math.pow(2, attempt));
        }
      }
      if (lastErr) throw lastErr;

      // 0..90% durante o envio dos chunks
      const chunkPercent = ((i + 1) / totalChunks) * 90;
      report(chunkPercent, "uploading");
    }

    // 3) complete (backend faz stream ao object storage)
    report(92, "finalizing");
    const { data: media } = await api.post("/media/chunk/complete", { session_id: sessionId }, {
      timeout: 30 * 60_000, // 30 min de folga para storage responder
    });
    report(100, "done");
    return media;
  } catch (e) {
    // best-effort: aborta a sessão para liberar disco no backend
    try {
      await api.post("/media/chunk/abort", { session_id: sessionId }, { timeout: 15_000 });
    } catch (_) {}
    throw e;
  }
}
